import { useEffect, useRef, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { getRtcConfig, CandidateQueue, writeSdp, writeCandidate } from "../../lib/remoteSupportRtc";

// Remote Support console (ported from skuru): enter the customer's 6-digit
// code, watch their screen live, and — for a tab share — click and type for
// them over the control data channel.

interface JoinedSession {
  sessionId: string;
  tenantId: string;
  tenantName: string | null;
  customerEmail: string | null;
}

export function AdminRemoteSupport() {
  const [codeInput, setCodeInput] = useState("");
  const [joined, setJoined] = useState<JoinedSession | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [streamLive, setStreamLive] = useState(false);
  const [controlReady, setControlReady] = useState(false);
  const [controlOn, setControlOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const interactRef = useRef<HTMLDivElement | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const unsubSignalsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void interactRef.current?.requestFullscreen().catch(() => undefined);
    }
  }

  function teardownRtc() {
    unsubSignalsRef.current?.();
    unsubSignalsRef.current = null;
    dcRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreamLive(false);
    setControlReady(false);
  }

  function sendControl(msg: Record<string, unknown>): void {
    const dc = dcRef.current;
    if (dc && dc.readyState === "open") {
      try {
        dc.send(JSON.stringify(msg));
      } catch {
        /* channel died mid-send */
      }
    }
  }

  // Map viewer coordinates to normalized capture coords, correcting for
  // object-contain letterboxing. Null outside the video content.
  function mapToNorm(clientX: number, clientY: number): { x: number; y: number } | null {
    const video = videoRef.current;
    if (!video) return null;
    const rect = video.getBoundingClientRect();
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || rect.width === 0 || rect.height === 0) return null;
    const scale = Math.min(rect.width / vw, rect.height / vh);
    const cw = vw * scale;
    const ch = vh * scale;
    const ox = rect.left + (rect.width - cw) / 2;
    const oy = rect.top + (rect.height - ch) / 2;
    const x = (clientX - ox) / cw;
    const y = (clientY - oy) / ch;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  }

  // Capture admin input over the viewer and forward it.
  useEffect(() => {
    const el = interactRef.current;
    if (!el || !controlOn || !streamLive) return;
    let lastMove = 0;

    const onMove = (e: MouseEvent) => {
      const nowTs = performance.now();
      if (nowTs - lastMove < 33) return;
      lastMove = nowTs;
      const p = mapToNorm(e.clientX, e.clientY);
      if (p) sendControl({ t: "pointer", ...p });
    };
    const onClick = (e: MouseEvent) => {
      const p = mapToNorm(e.clientX, e.clientY);
      if (p) {
        el.focus();
        sendControl({ t: "click", ...p });
      }
    };
    const onDblClick = (e: MouseEvent) => {
      const p = mapToNorm(e.clientX, e.clientY);
      if (p) sendControl({ t: "dblclick", ...p });
    };
    const onWheel = (e: WheelEvent) => {
      const p = mapToNorm(e.clientX, e.clientY);
      if (!p) return;
      e.preventDefault();
      sendControl({ t: "scroll", ...p, dx: e.deltaX, dy: e.deltaY });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return; // leave browser shortcuts alone
      e.preventDefault();
      if (e.key.length === 1) sendControl({ t: "type", text: e.key });
      else sendControl({ t: "key", key: e.key });
    };
    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData("text");
      if (text) {
        e.preventDefault();
        sendControl({ t: "type", text });
      }
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("click", onClick);
    el.addEventListener("dblclick", onDblClick);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("paste", onPaste);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("click", onClick);
      el.removeEventListener("dblclick", onDblClick);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("paste", onPaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlOn, streamLive]);

  // Watch the joined session for the customer ending it.
  useEffect(() => {
    if (!joined) return;
    return onSnapshot(doc(db, paths.remoteSupportSession(joined.sessionId)), (snap) => {
      const s = (snap.get("status") as string | undefined) ?? null;
      setStatus(s);
      if (s === "ended" || s === "expired") teardownRtc();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  // Answer the customer's WebRTC offer and render their stream.
  useEffect(() => {
    if (!joined || status !== "connected") return;
    let cancelled = false;
    let pc: RTCPeerConnection | null = null;
    let unsub: (() => void) | null = null;

    void (async () => {
      const config = await getRtcConfig();
      if (cancelled) return;
      pc = new RTCPeerConnection(config);
      pcRef.current = pc;
      const queue = new CandidateQueue(pc);
      let answered = false;

      pc.ontrack = (e) => {
        const stream = e.streams[0] ?? new MediaStream([e.track]);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => undefined);
        }
        setStreamLive(true);
      };
      pc.ondatachannel = (e) => {
        dcRef.current = e.channel;
        e.channel.onopen = () => setControlReady(true);
        e.channel.onclose = () => setControlReady(false);
        e.channel.onmessage = (m) => {
          try {
            if (JSON.parse(String(m.data))?.t === "noControl") {
              setControlReady(false);
              setControlOn(false);
              setError(
                "The customer shared a whole window or screen, so remote control is off. " +
                  "Ask them to re-share and pick the Clevis tab if you need to click for them.",
              );
            }
          } catch {
            /* ignore */
          }
        };
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) void writeCandidate(joined.sessionId, "admin", e.candidate);
      };

      unsub = onSnapshot(collection(db, paths.remoteSupportSignals(joined.sessionId)), (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type !== "added" && change.type !== "modified") continue;
          const d = change.doc;
          const data = d.data();
          if (d.id === "offer" && !answered && pc) {
            answered = true;
            const peer = pc;
            void (async () => {
              try {
                await peer.setRemoteDescription({ type: "offer", sdp: (data.sdp as string) ?? "" });
                await queue.markReady();
                const answer = await peer.createAnswer();
                await peer.setLocalDescription(answer);
                await writeSdp(joined.sessionId, "answer", answer);
              } catch {
                setError("Couldn't establish the screen-share connection.");
              }
            })();
          } else if (data.role === "customer" && data.candidate) {
            void queue.add(data.candidate as RTCIceCandidateInit);
          }
        }
      });
      unsubSignalsRef.current = unsub;
    })();

    return () => {
      cancelled = true;
      unsub?.();
      pc?.close();
      if (pcRef.current === pc) pcRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, status === "connected"]);

  async function handleJoin() {
    const code = codeInput.replace(/[^0-9]/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit code the customer reads to you.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await call<JoinedSession>("joinRemoteSupportSession", { code }, { mutation: false });
      setJoined(res);
      setStatus("connected");
      setCodeInput("");
    } catch (err) {
      setError((err as Error).message ?? "Couldn't join with that code.");
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd() {
    if (!joined) return;
    setBusy(true);
    try {
      await call("endRemoteSupportSession", { sessionId: joined.sessionId }, { mutation: false });
    } catch {
      /* the doc listener reflects reality */
    } finally {
      setBusy(false);
    }
  }

  const sessionOver = status === "ended" || status === "expired";

  return (
    <div className={joined ? "space-y-4" : "mx-auto max-w-2xl space-y-4"}>
      <h1 className="text-lg font-bold">Remote Support</h1>
      {!joined ? (
        <Card>
          <CardTitle>Join a customer session</CardTitle>
          <p className="mb-3 text-xs text-steel">
            Ask the customer to open <strong>Support</strong> → <strong>Start remote session</strong>,
            then enter the 6-digit code they read to you. Codes are single-use and expire after 10 minutes.
          </p>
          <div className="flex items-center gap-3">
            <Input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/[^0-9\-\s]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleJoin();
              }}
              placeholder="000-000"
              inputMode="numeric"
              className="w-44 font-mono text-xl tracking-[0.25em]"
            />
            <Button onClick={() => void handleJoin()} busy={busy}>
              Join session
            </Button>
          </div>
          {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
        </Card>
      ) : (
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className={cn("inline-block h-2.5 w-2.5 rounded-full", sessionOver ? "bg-steel/40" : "bg-success")} />
                <span className="text-base font-medium text-ink">{joined.tenantName ?? joined.tenantId}</span>
              </div>
              <div className="mt-1 text-xs text-steel">
                {joined.customerEmail ?? "Customer"} ·{" "}
                {sessionOver ? (status === "expired" ? "Session expired." : "Session ended.") : "Connected."}
              </div>
            </div>
            <div className="flex gap-2">
              {!sessionOver ? (
                <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void handleEnd()} disabled={busy}>
                  End session
                </Button>
              ) : (
                <Button className="px-3 py-1 text-xs" onClick={() => setJoined(null)}>
                  Join another
                </Button>
              )}
            </div>
          </div>

          {!sessionOver ? (
            <>
              {streamLive ? (
                <div className="mb-2 flex flex-wrap items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => setControlOn((v) => !v)}
                    disabled={!controlReady}
                    className={cn(
                      "rounded-md px-3 py-1.5 font-medium",
                      controlOn && controlReady ? "bg-brandorange text-white" : "bg-cream text-steel",
                    )}
                  >
                    {controlReady ? (controlOn ? "Control: ON" : "Control: OFF") : "Control unavailable"}
                  </button>
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className="rounded-md bg-cream px-3 py-1.5 font-medium text-steel hover:bg-steel/20"
                  >
                    {isFullscreen ? "Exit fullscreen" : "⛶ Fullscreen"}
                  </button>
                  <span className="text-steel">
                    {controlOn && controlReady
                      ? "Click the screen below to control their Clevis tab — typing and scrolling go to them while it's focused."
                      : "View-only. Turn control on to click and type for them."}
                  </span>
                </div>
              ) : null}
              <div
                ref={interactRef}
                tabIndex={0}
                className={cn(
                  "relative overflow-hidden rounded-lg border bg-ink outline-none",
                  controlOn && controlReady && streamLive
                    ? "cursor-crosshair border-brandorange focus:ring-2 focus:ring-brandorange"
                    : "border-steel/25",
                )}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={cn("pointer-events-none w-full object-contain", isFullscreen ? "h-full" : "max-h-[85vh]")}
                />
                {!streamLive ? (
                  <div className="absolute inset-0 flex items-center justify-center p-10 text-center text-sm text-steel-light">
                    Waiting for the customer to press "Share your screen" on their Support page…
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <p className="text-xs text-steel">
              The code is burned — reconnecting needs a fresh session from the customer.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
